import FetchHelper from '../helpers/fetch_helper';
import Actions from '../actions/actions';
import FunctionalityAccessHelper from '../helpers/functionality_access_helper';
import dispatcher from './dispatcher';
import FeaturePermissionsHelper from '../helpers/feature_permissions_helper';

const UsersDispatcher = {
  async fetchVerifiedSigners() {
    try {
      const promise = await FetchHelper.fetchJson('verified-signers', { method: 'GET' });
      this.setStoreVal('verifiedSigners', promise.results);
    } catch (error) {
      this.dispatch({
        type: 'handleRequestError',
        data: { error, defaultErrorMessage: 'Failed to fetch verified signers emails' },
      });
    }
  },

  clearVerifiedSigners() {
    this.setStoreVal('verifiedSigners', null);
  },

  updateSendForSignatureList({ signers, docs, submitted }) {
    this.setStoreVal('sendForSignatureList', { signers, docs, submitted });
  },

  clearSendForSignatureList() {
    this.setStoreVal('sendForSignatureList', { signers: [], docs: [], submitted: false });
  },

  async fetchUserList({ isActive, coid }) {
    if (
      !FeaturePermissionsHelper.hasPermission(this.getStoreVal('user'), 'USER', 'READ') &&
      !window.__aboutToImpersonate &&
      !['CPF', 'A'].includes(this.getStoreVal('user')?.atype)
    ) {
      Actions.setErrorMessages(['Page not found']);
      return;
    }

    let userList;
    if (coid === null || coid === undefined || coid === '') {
      userList = (await FetchHelper.fetchJson(`users${isActive ? '' : '/inactive'}`, { method: 'get' }))?.results;
      await this.setStoreVal(`${isActive ? '' : 'in'}activeUserList`, userList);
      return userList;
    }

    userList = (await FetchHelper.fetchJson(`companies/${coid}/users${isActive ? '' : '/inactive'}`, { method: 'get' }))
      ?.results;

    await this.setStoreVal(`${isActive ? '' : 'in'}activeUserList`, userList);
    return userList;
  },

  async clearActiveUserList() {
    await this.setStoreVal(`activeUserList`, null);
  },

  async clearInactiveUserList() {
    await this.setStoreVal(`inactiveUserList`, null);
  },

  async getBranchList(coid) {
    let results = (await FetchHelper.fetchJson(`companies/${coid}/branches`, { method: 'GET' })).results;
    this.setStoreVal('branchList', results);
    return results;
  },

  async clearBranchList() {
    this.setStoreVal('branchList', null);
  },

  async getBranch(data) {
    let branchPromise = Promise.resolve({
      results: [
        {
          id: null,
          coid: data.coid,
          branchname: '',
          address1: '',
          address2: '',
          city: '',
          state: '',
          zip: '',
        },
      ],
    });

    if (data?.branchId) {
      branchPromise = FetchHelper.fetchJson(`companies/${data.coid}/branches/${data.branchId}`, {
        method: 'GET',
      });
    }

    const branch = (await Promise.all([branchPromise]))?.[0].results?.[0];

    this.setStoreVal('foundBranch', branch);
    return branch;
  },

  async createBranch(data) {
    const view = this.getStoreVal('view');

    const newBranch = (
      await FetchHelper.fetchJson(`companies/${view.coid}/branches`, {
        method: 'POST',
        body: JSON.stringify(data),
      })
    )?.results;

    this.setStoreVal('qaBranch', {
      id: newBranch?.[0]?.id,
      coid: data.coid,
    });

    return newBranch;
  },

  async updateBranch(data) {
    const view = this.getStoreVal('view');

    return (
      await FetchHelper.fetchJson(`companies/${view.coid}/branches`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      })
    ).results;
  },

  async deleteBranch(data) {
    const view = this.getStoreVal('view');

    const results = (
      await FetchHelper.fetchJson(`companies/${view.coid}/branches/${data}`, {
        method: 'DELETE',
        body: JSON.stringify(data),
      })
    )?.results;

    dispatcher.dispatch({
      type: 'getBranchList',
      data: view.coid,
    });

    return results;
  },

  async hardDeleteBranch(data) {
    const view = this.getStoreVal('view');

    const results = (
      await FetchHelper.fetchJson(`companies/${view.coid}/branches/${data}?hardDelete=true`, {
        method: 'DELETE',
      })
    )?.results;

    dispatcher.dispatch({
      type: 'getBranchList',
      data: view.coid,
    });
    return results;
  },

  async clearBranch() {
    this.setStoreVal('foundBranch', null);
  },

  async createPricingGroup(data) {
    data.pricingVisibility = data.pricingVisibility.map((rate) => {
      return {
        ...rate,
        visibility: true,
      };
    });

    let newGroup = (
      await FetchHelper.fetchJson(`companies/${data.coid}/pricingGroup`, {
        method: 'POST',
        body: JSON.stringify(data),
      })
    ).results;

    this.setStoreVal('qaPricingGroup', {
      id: newGroup?.[0]?.id,
      coid: data.coid,
    });

    return newGroup;
  },

  async updatePricingGroup(data) {
    data.pricingVisibility = data.pricingVisibility.map((rate) => {
      return {
        ...rate,
        visibility: true,
      };
    });

    return (
      await FetchHelper.fetchJson(`companies/${data.coid}/pricingGroup/${data.id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      })
    ).results;
  },

  async clearPricingGroupList() {
    this.setStoreVal('pricingGroupsList', null);
  },

  async getPricingGroupList(coid) {
    let results = (await FetchHelper.fetchJson(`companies/${coid}/pricingGroups`, { method: 'GET' })).results;

    this.setStoreVal('pricingGroupsList', results);
    return results;
  },

  async getPricingGroup(data) {
    let pricingGroupPromise = Promise.resolve({
      results: [
        {
          id: null,
          coid: data.coid,
          label: '',
          pricingVisibility: [],
        },
      ],
    });

    if (data?.pricingId && data?.pricingId !== 'invalid') {
      pricingGroupPromise = FetchHelper.fetchJson(`companies/${data.coid}/pricingGroup/${data.pricingId}`, {
        method: 'GET',
      });
    }

    let rateListPromise = this.dispatch({
      type: 'getRatesList',
      data: data.coid,
      emptyIsStillData: true,
    });

    const pricingGroup = (await Promise.all([pricingGroupPromise, rateListPromise]))?.[0].results?.[0];

    this.setStoreVal('foundPricingGroup', pricingGroup);

    return pricingGroup;
  },

  async clearPricingGroup() {
    this.setStoreVal('foundPricingGroup', null);
    this.setStoreVal('ratesList', null);
  },

  async createLicense(data) {
    let newLicense = (
      await FetchHelper.fetchJson(`companies/${data.coid}/licenses`, {
        method: 'POST',
        version: '/v2',
        body: JSON.stringify({
          ...data,
          branchId: data.branchId === 'all' ? null : data.branchId,
        }),
      })
    ).results;

    this.setStoreVal('qaLicense', {
      id: newLicense?.[0]?.id,
      coid: data.coid,
    });

    return newLicense;
  },

  async updateLicense(data) {
    return (
      await FetchHelper.fetchJson(`companies/${data.coid}/licenses/${data.id}`, {
        method: 'PUT',
        version: '/v2',
        body: JSON.stringify({
          ...data,
          branchId: data.branchId === 'all' ? null : data.branchId,
        }),
      })
    ).results;
  },

  async bulkUpdateLicenses(data) {
    try {
      let results = (
        await FetchHelper.fetchJson(`companies/${data.coid}/licenses`, {
          method: 'PUT',
          version: '/v2',
          body: JSON.stringify(data.newLicenses),
        })
      ).results;

      this.setStoreVal('licenseList', results);
      return results;
    } catch (error) {
      return error?.response?.clone()?.text() || error.message;
    }
  },

  async clearLicenseList() {
    this.setStoreVal('licenseList', null);
  },

  async getLicenseList(coid) {
    let results = (
      await FetchHelper.fetchJson(`companies/${coid}/licenses`, {
        method: 'GET',
        version: '/v2',
      })
    ).results;

    this.setStoreVal('licenseList', results);
    return results;
  },

  async getUserLicenseList({ coid, branchId }) {
    const url = branchId
      ? `companies/${coid}/branches/${branchId}/licenses`
      : `companies/${coid}/licenses?enforceNullBranch=true`;
    const results = (await FetchHelper.fetchJson(url, { method: 'GET', version: '/v2' })).results;

    this.setStoreVal('userLicenseList', results);
    return results;
  },

  async getLicense(data) {
    let licensePromise = Promise.resolve({
      results: [
        {
          id: null,
          licenseNumber: '',
          coid: data.coid,
          branchId: 'all',
          state: 'none',
        },
      ],
    });

    if (data?.licenseId) {
      licensePromise = FetchHelper.fetchJson(`companies/${data.coid}/licenses/${data.licenseId}`, {
        method: 'GET',
        version: '/v2',
      });
    }

    const license = (await Promise.all([licensePromise]))?.[0].results?.[0];

    if (license.branchId === null) {
      license.branchId = 'all';
    }

    this.setStoreVal('foundLicense', license);
    return license;
  },

  async clearLicense() {
    this.setStoreVal('foundLicense', null);
  },

  async deleteLicense({ id, coid }) {
    const results = (
      await FetchHelper.fetchJson(`companies/${coid}/licenses/${id}`, {
        method: 'DELETE',
        version: '/v2',
      })
    )?.results;

    dispatcher.dispatch({
      type: 'getLicenseList',
      data: coid,
    });

    return results;
  },

  async hardDeleteLicense({ id, coid }) {
    const results = (
      await FetchHelper.fetchJson(`companies/${coid}/licenses/${id}?hardDelete=true`, {
        method: 'DELETE',
        version: '/v2',
      })
    )?.results;

    dispatcher.dispatch({
      type: 'getLicenseList',
      data: coid,
    });

    return results;
  },

  async getRatesList(coid) {
    let calculationModels = (await FetchHelper.fetchJson(`companies/${coid}/pricingVisibilities`, { method: 'GET' }))
      ?.results;

    await calculationModels.map((rate) => {
      return {
        ...rate,
        name: rate.label,
        selected: false,
      };
    });

    this.setStoreVal('ratesList', calculationModels);
  },

  async fetchUser(userid) {
    let currUser = await this.getStoreVal('user');
    if ((!window.__aboutToImpersonate && currUser === null) || !FunctionalityAccessHelper.hasAccessToAdmin(currUser)) {
      Actions.setErrorMessages(['Page not found']);
      Actions.setRoute('/proposal/new');
      return;
    }

    let foundUser = (await FetchHelper.fetchJson(`users/${userid}`, { method: 'GET' })).results[0];

    if (foundUser.branch.id === null) {
      foundUser.branch.id = 'none';
    }

    this.setStoreVal('foundUser', foundUser);

    let promiseList = [
      {
        type: 'getBranchList',
        data: foundUser.company.coid,
        emptyIsStillData: true,
      },
      {
        type: 'getPricingGroupList',
        data: foundUser.company.coid,
        emptyIsStillData: true,
      },
      {
        type: 'getRatesList',
        data: foundUser.company.coid,
        emptyIsStillData: true,
      },
    ];

    if (foundUser.atype === 'CPF') {
      this.setStoreVal('pricingGroupsList', '');
      this.setStoreVal('ratesList', '');
      this.setStoreVal('foundPricingGroup', '');
      promiseList.splice(1, 2);
    }

    await Promise.all(promiseList.map((promise) => this.dispatch(promise)));
    return foundUser;
  },

  async fetchUserByCustomer(customerId) {
    let foundUser = (await FetchHelper.fetchJson(`users/customer/${customerId}`, { method: 'GET' })).results[0];
    return foundUser;
  },

  async fetchCompanyList() {
    this.setStoreVal('companyList', (await FetchHelper.fetchJson('companies', { method: 'get' }))?.results);
  },

  async fetchCompany(coid) {
    let companyPromise = Promise.resolve({
      results: [
        {
          coid: null,
          address1: '',
          address2: '',
          city: '',
          state: '',
          zip: '',
          fein: '',
          gcInsuranceCarrier: null,
          gcInsuranceCarrierPhone: null,
          gcInsuranceType: null,
          isTest: false,
          trainingRequired: false,
          name: '',
          type: '',
        },
      ],
    });

    if (coid) {
      companyPromise = FetchHelper.fetchJson(`companies/${coid}`, { method: 'get' });
    }

    const company = (await Promise.all([companyPromise]))?.[0].results?.[0];

    this.setStoreVal('company', company);
    return company;
  },

  async fetchCompanyAdminContactInfo(coid) {
    const AdminInfoPromise = FetchHelper.fetchJson(`companies/${coid}/admin-contact-info`, {
      method: 'get',
      version: '/v3',
    });

    return (await Promise.resolve(AdminInfoPromise))?.results?.[0];
  },

  async fetchCompanyWithAdmin(coid) {
    let companyPromise = Promise.resolve({
      results: [
        {
          company: {
            coid: null,
            address1: '',
            address2: '',
            city: '',
            state: '',
            zip: '',
            fein: '',
            functionalityAcls: [],
            gcInsuranceCarrier: null,
            gcInsuranceCarrierPhone: null,
            gcInsuranceType: null,
            isTest: false,
            trainingRequired: true,
            mfaRequired: true,
            name: '',
            type: '',
            pricingProgram: 'none',
          },
          admin: {},
          companyElements: {},
        },
      ],
    });

    if (coid) {
      companyPromise = FetchHelper.fetchJson(`companies/${coid}/with-admin`, { method: 'get' });
    }

    const company = (await companyPromise)?.results?.[0];

    this.setStoreVal('company', company);
    return company;
  },

  async fetchCompanyElementTypeForm(coid) {
    const queryParams = coid ? `?companyId=${coid}` : '';
    const companyElementTypes = (
      await FetchHelper.fetchJson(`companies/company-element-types${queryParams}`, { method: 'get' })
    )?.results;

    this.setStoreVal('companyElementTypes', companyElementTypes);
    return companyElementTypes;
  },

  async fetchCompanyPaymentPrograms(coid) {
    const queryParams = coid ? `?companyId=${coid}` : '';
    const companyPaymentPrograms = (
      await FetchHelper.fetchJson(`companies/payment-programs/available${queryParams}`, { method: 'get' })
    )?.results;
    this.setStoreVal('companyPaymentPrograms', companyPaymentPrograms);
    return companyPaymentPrograms;
  },

  async fetchCompanyAuditLogs(coid) {
    let auditLogs = [];

    if (coid) {
      auditLogs = (await FetchHelper.fetchJson(`companies/${coid}/audit-logs`, { method: 'get', version: '/v3' }))
        ?.results;
    }
    this.setStoreVal('auditLogs', auditLogs);
    return auditLogs;
  },

  async clearCompanyList() {
    this.setStoreVal('companyList', null);
  },

  async clearCompany() {
    this.setStoreVal('company', null);
    this.setStoreVal('auditLogs', null);
    this.setStoreVal('companyElementTypes', null);
    this.setStoreVal('companyPaymentPrograms', null);
  },

  async createCompany(companyData) {
    try {
      const createdCompany = (
        await FetchHelper.fetchJson(`companies`, {
          method: 'POST',
          body: JSON.stringify(companyData),
        })
      )?.results?.[0];
      this.setStoreVal('qaCreatedCompany', createdCompany);
    } catch (error) {
      return error?.response?.clone()?.text() || error.message;
    }
  },

  async updateCompany({ companyData, coid }) {
    try {
      const data = (
        await FetchHelper.fetchJson(`companies/${coid}`, {
          method: 'PATCH',
          body: JSON.stringify(companyData),
        })
      ).results?.[0];
      this.setStoreVal('company', {
        ...data,
        company: { ...data?.company, isTest: !!companyData.isTest },
      });
    } catch (error) {
      return error?.response?.clone()?.text() || error.message;
    }
  },

  clearUser() {
    this.setStoreVal('foundUser', null);
    this.setStoreVal('branchList', null);
  },

  async newUser(coid) {
    let currUser = await this.getStoreVal('user');

    let response = (
      await FetchHelper.fetchJson(`companies/${!!coid ? coid : currUser.company.coid}`, { method: 'get' })
    ).results[0];

    this.setStoreVal('foundUser', {
      firstname: '',
      lastname: '',
      username: '',
      branch: { id: 'invalid' },
      viewall: 'OWN',
      active: true,
      company: response,
      functionalityAcls: [],
      pricingVisibility: [],
      opsMgr: false,
      pricingGroupId: 'invalid',
    });

    await this.dispatch({
      type: 'getBranchList',
      data: response.coid,
      emptyIsStillData: true,
    });

    await this.dispatch({
      type: 'getPricingGroupList',
      data: response.coid,
      emptyIsStillData: true,
    });

    await this.dispatch({
      type: 'getRatesList',
      data: response.coid,
      emptyIsStillData: true,
    });
  },
  async createUser(data) {
    let currUser = await this.getStoreVal('user');
    let view = await this.getStoreVal('view');

    let tmpUser = JSON.parse(JSON.stringify(data));

    tmpUser.pricingVisibility.forEach((a) => (a.visibility = true));

    try {
      if (tmpUser.branch.id === 'none') {
        tmpUser.branch.id = null;
      }

      if (tmpUser?.mobilePhone) {
        tmpUser.mobilePhone = `1${tmpUser?.mobilePhone}`;
      }

      tmpUser.company.coid = view.coid;
      let response = await FetchHelper.fetchJson('users/', {
        method: 'POST',
        body: JSON.stringify(tmpUser),
      });
      dispatcher.dispatch({
        type: 'fetchUserList',
        data: { isActive: true, coid: !!view.coid ? view.coid : null },
      });

      const newUser = response?.results;

      this.setStoreVal('qaUser', {
        userid: newUser?.[0]?.userid,
        tempPassword: newUser?.[0]?.userSettingValues?.find(({ setting }) => setting === 'temp_password')?.value,
      });

      if (response.status === 'success') {
        //wait for confirmation
        currUser.atype === 'CPF' && !!view.coid
          ? Actions.setRoute(`/account/company/${tmpUser.company.coid}/${tmpUser.active ? 'active' : 'inactive'}`)
          : Actions.setRoute(`/account/users/active`);
        Actions.setMessages(['User added']);
      }
    } catch (error) {
      return error?.response?.clone()?.text() || error.message;
    }
  },
  async updateUser(data) {
    let currUser = await this.getStoreVal('user');
    let view = await this.getStoreVal('view');

    let tmpUser = JSON.parse(JSON.stringify(data));
    tmpUser.pricingVisibility.forEach((a) => (a.visibility = true));

    try {
      if (tmpUser.branch.id === 'none') {
        tmpUser.branch.id = null;
      }

      if (tmpUser?.mobilePhone && !tmpUser.mobilePhone.startsWith('+')) {
        tmpUser.mobilePhone = `1${tmpUser?.mobilePhone}`;
      }

      await FetchHelper.fetchJson(`users/${tmpUser.userid}`, {
        method: 'PATCH',
        body: JSON.stringify(tmpUser),
      });

      Actions.setMessages(['User updated']);

      currUser.atype === 'CPF' && !!view.coid
        ? Actions.setRoute(`/account/company/${tmpUser.company.coid}/${tmpUser.active ? 'active' : 'inactive'}`)
        : Actions.setRoute(`/account/users/active`);
    } catch (error) {
      return error.response.clone().text();
    }
  },
  async deleteUser(data) {
    let view = await this.getStoreVal('view');

    await FetchHelper.fetchJson(`users/${data}`, {
      method: 'delete',
      body: JSON.stringify(data),
    });
    dispatcher.dispatch({
      type: 'fetchUserList',
      data: { isActive: true, coid: !!view.coid ? view.coid : null },
    });

    Actions.setMessages(['User has been deactivated']);
  },
  async activateUser(data) {
    let view = await this.getStoreVal('view');

    await FetchHelper.fetchJson(`users/inactive/${data}`, {
      method: 'delete',
    });

    dispatcher.dispatch({
      type: 'fetchUserList',
      data: { isActive: false, coid: !!view.coid ? view.coid : null },
    });
    Actions.setMessages(['User has been activated']);
  },

  async hardDeleteUser(data) {
    await FetchHelper.fetchJson(`users/${data}?hardDelete=true`, {
      method: 'delete',
    });

    dispatcher.dispatch({
      type: 'fetchUserList',
      data: { isActive: true, coid: null },
    });

    dispatcher.dispatch({
      type: 'fetchUserList',
      data: { isActive: false, coid: null },
    });
  },

  async trainUser({ userid, date }) {
    await FetchHelper.fetchJson(`users/${userid}/userAgreementComp`, {
      method: 'PATCH',
      body: JSON.stringify({ date }),
    });
  },

  async clearAuthorizedSigner() {
    this.setStoreVal('foundAuthorizedSigner', null);
  },

  async fetchAuthorizedSigner({ id, coid }) {
    let authorizedSignerPromise = Promise.resolve({
      results: [
        {
          id: null,
          firstName: '',
          lastName: '',
          emailAddress: '',
          branchId: 'all',
          default: false,
          coid: coid,
          description: '',
          status: 'pending',
        },
      ],
    });

    if (id) {
      authorizedSignerPromise = FetchHelper.fetchJson(`verified-signers/${id}`, {
        method: 'GET',
      });
    }
    const authorizedSigner = (await authorizedSignerPromise)?.results?.[0];
    if (authorizedSigner.branchId === null) {
      authorizedSigner.branchId = 'none';
    }

    this.setStoreVal('foundAuthorizedSigner', authorizedSigner);
    return authorizedSigner;
  },

  async createAuthorizedSigner(data) {
    const signer = (
      await FetchHelper.fetchJson(`verified-signers`, {
        method: 'POST',
        body: JSON.stringify({
          ...data,
          branchId: ['all', 'none'].includes(data.branchId) ? null : data.branchId,
        }),
      })
    ).results;

    this.setStoreVal('qaSigner', {
      id: signer?.[0]?.id,
      emailAddress: signer?.[0]?.emailAddress,
    });

    return signer;
  },

  async updateAuthorizedSigner(data) {
    const signer = (
      await FetchHelper.fetchJson(`verified-signers/${data.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          ...data,
          branchId: ['all', 'none'].includes(data.branchId) ? null : data.branchId,
        }),
      })
    ).results;

    this.setStoreVal('qaSigner', {
      id: signer?.[0]?.id,
      emailAddress: signer?.[0]?.emailAddress,
    });

    return signer;
  },

  async fetchAuthorizedSignerList(data) {
    const authorizedSignerList = (
      await FetchHelper.fetchJson(`verified-signers/companies/${data.coid}`, { method: 'get' })
    )?.results;
    this.setStoreVal('authorizedSignerList', authorizedSignerList);
    return authorizedSignerList;
  },

  async clearAuthorizedSignerList() {
    this.setStoreVal('authorizedSignerList', null);
  },

  async deleteSigner({ id, coid }) {
    let response = await FetchHelper.fetchJson(`verified-signers/${id}?hardDelete=false`, {
      method: 'delete',
    });

    if (response?.messages?.length) {
      Actions.setErrorMessages('Default authorized signer removed');
    } else {
      Actions.setMessages('Authorized signer removed');
    }

    dispatcher.dispatch({
      type: 'fetchAuthorizedSignerList',
      data: { coid },
    });
  },

  async hardDeleteSigner({ id, coid }) {
    await FetchHelper.fetchJson(`verified-signers/${id}?hardDelete=true`, {
      method: 'delete',
    });

    dispatcher.dispatch({
      type: 'fetchAuthorizedSignerList',
      data: { coid },
    });
  },

  async getSubscriptionList(coid) {
    let results = (await FetchHelper.fetchJson(`subscriptions/company/${coid}`, { method: 'GET' })).results;
    this.setStoreVal('subscriptionList', results);
    return results;
  },

  async clearSubscriptionList() {
    this.setStoreVal('subscriptionList', null);
  },

  async getSubscription(data) {
    let subscriptionPromise = Promise.resolve({
      results: [
        {
          id: null,
          role: 'admin',
          method: 'email',
          eventType: 'customer_status_change',
          additionalSubscriptionInfo: {},
          scopes: [],
        },
      ],
    });

    if (data?.subscriptionId) {
      subscriptionPromise = FetchHelper.fetchJson(`subscriptions/${data.subscriptionId}`, {
        method: 'GET',
      });
    }

    const subscription = (await subscriptionPromise)?.results?.[0];

    this.setStoreVal('foundSubscription', subscription);
    return subscription;
  },

  async createSubscription({ subscription, coid }) {
    const newSubscription = (
      await FetchHelper.fetchJson(`subscriptions/company/${coid}`, {
        method: 'POST',
        body: JSON.stringify(subscription),
      })
    )?.results;

    this.setStoreVal('qaSubscription', {
      id: newSubscription?.[0]?.id,
      email: newSubscription?.[0]?.additionalSubscriptionInfo?.emailAddress,
      role: newSubscription?.[0]?.role,
      eventType: newSubscription?.[0]?.eventType,
    });

    return newSubscription;
  },

  async updateSubscription(data) {
    const updatedSubscription = (
      await FetchHelper.fetchJson(`subscriptions`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      })
    )?.results;

    this.setStoreVal('qaSubscription', {
      id: updatedSubscription?.[0]?.id,
      email: updatedSubscription?.[0]?.additionalSubscriptionInfo?.emailAddress,
      role: updatedSubscription?.[0]?.role,
      eventType: updatedSubscription?.[0]?.eventType,
    });

    return updatedSubscription;
  },

  async deleteSubscription({ id, coid }) {
    const results = (
      await FetchHelper.fetchJson(`subscriptions/${id}`, {
        method: 'DELETE',
      })
    )?.results;

    dispatcher.dispatch({
      type: 'getSubscriptionList',
      data: coid,
    });

    return results;
  },

  async hardDeleteSubscription({ id, coid }) {
    const results = (
      await FetchHelper.fetchJson(`subscriptions/${id}/companies/${coid}?hardDelete=true`, {
        method: 'DELETE',
      })
    )?.results;

    dispatcher.dispatch({
      type: 'getSubscriptionList',
      data: coid,
    });
    return results;
  },

  async clearSubscription() {
    this.setStoreVal('foundSubscription', null);
  },

  async fetchZip({ zip, setZipError, consentToken }) {
    try {
      return (
        await FetchHelper.fetchJson(
          `${consentToken ? `homeowner/locations/${consentToken}` : 'locations'}?zipcodeFallback=${zip}`,
          { method: 'get' }
        )
      )?.results?.[0];
    } catch (e) {
      // return setZipError('Could not find ZIP Code');
    }
  },

  async migrateCompanyProduct(data) {
    try {
      const results = (
        await FetchHelper.fetchJson(`companies/change-product`, {
          method: 'PUT',
          body: JSON.stringify(data),
        })
      )?.results;

      if (results?.[0]?.failedUpdates === 0) {
        Actions.setMessages(['Migration complete!']);
      } else {
        Actions.setErrorMessages(['Error migrating Company to new product']);
      }
      return results;
    } catch (error) {
      Actions.setErrorMessages(['Error migrating Company to new product']);
    }
  },

  async getSuperAdminToolsData() {
    return {
      user: this.getStoreVal('user'),
      financingRequest: this.getStoreVal('financingRequest'),
      customer: this.getStoreVal('customer'),
      proposal: this.getStoreVal('proposal'),
      financingMethod: this.getStoreVal('financingMethod'),
      eligibility: this.getStoreVal('eligibility'),
    };
  },

  async fetchLatestTrainingResource() {
    try {
      return (await FetchHelper.fetchJson('training-resources/latest'))?.results?.[0];
    } catch (error) {
      this.dispatch({
        type: 'handleRequestError',
        data: { error, defaultErrorMessage: 'Unable to load training, please contact Concert Support' },
      });
      return null;
    }
  },

  async patchUserTraining({ userId, sku, dateTrained, status }) {
    try {
      await FetchHelper.fetchJson(`users/${userId}/training`, {
        method: 'PATCH',
        body: JSON.stringify({ sku, dateTrained, status }),
      });
      return true;
    } catch (e) {
      Actions.setErrorMessages('Unable to complete training, please contact Concert Support');
      return false;
    }
  },

  async fetchUserFinancingMethods({ amountFinanced, cstate, prePaymentPercent }) {
    try {
      const userFinancingMethods = await FetchHelper.fetchJson(`financing-methods`, {
        method: 'post',
        version: '/v3',
        body: JSON.stringify({ amountFinanced, cstate, prePayments: [{ percent: prePaymentPercent }] }),
      });
      return userFinancingMethods.results;
    } catch (error) {
      this.dispatch({
        type: 'handleRequestError',
        data: { error, defaultErrorMessage: 'Something went wrong while fetching products' },
      });
      return null;
    }
  },
};
export default UsersDispatcher;
